import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import useRevealSequence from "../useRevealSequence";

/**
 * Neon Energy Portal - the board is drawn into a charging core.
 *
 * Built after docs/animation-samples/Neon_Energy_Portal.html. While the
 * round is still closing the yantras orbit an energy core, faster and
 * faster, with a scan flickering across them - the sample's "energy
 * charge" and "orbit acceleration", stretched to fill however long the
 * window is. When the result arrives the sample plays through: the
 * winner's card locks on, a beam comes down into the core, every card is
 * absorbed into it, lightning bursts and the portal opens with the
 * winner materializing inside, crowned.
 *
 * The cards are moved by writing their transforms directly, not through
 * React state - an orbit re-rendering the live page sixty times a second
 * is what the outlet's tablets cannot afford.
 */

const CX = 410;
const CY = 418;
const RX = 300;
const RY = 238;
const SPEED_START = 18; // degrees a second when the window opens
const SPEED_MAX = 75; // ...and what it builds up to
const SPEED_RAMP = 7; // degrees a second, per second
const SCAN_MS = 200;

// The landing, once the winner is known. Kept tight: the page takes the
// modal down a few seconds after the result, and the winner should be up
// for most of that.
const LOCK_MS = 280;
const BEAM_MS = 320;
const ABSORB_MS = 480;
const ABSORB_STAGGER = 22;
const PORTAL_MS = 220;
const HOLD_MS = 1700;

const PARTICLE_GLYPHS = ["⚡", "✨", "💎", "🔷", "⭐"];
const PARTICLE_COUNT = 44;

const easeOut = (p) => 1 - Math.pow(1 - p, 4);
const random = (min, max) => min + Math.random() * (max - min);

const STATUS = {
  charging: "Energy core charging",
  locked: "Target locked",
  beam: "Energy beam",
  absorb: "Core overload",
  portal: "Portal opening",
  won: "Winner materialized",
};

function Particles({ seed }) {
  const bits = useMemo(
    () =>
      Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
        id: `${seed}-${i}`,
        glyph: PARTICLE_GLYPHS[i % PARTICLE_GLYPHS.length],
        x: random(-400, 400),
        y: random(-330, 300),
        r: random(-500, 500),
        delay: random(0, 0.2),
      })),
    [seed],
  );

  return (
    <div className="rv-np-particles" aria-hidden="true">
      {bits.map((b) => (
        <span
          key={b.id}
          style={{
            "--rv-x": `${b.x}px`,
            "--rv-y": `${b.y}px`,
            "--rv-r": `${b.r}deg`,
            animationDelay: `${b.delay}s`,
          }}
        >
          {b.glyph}
        </span>
      ))}
    </div>
  );
}

export default function NeonPortal({ tiles, winnerIndex, onFinished }) {
  const count = tiles.length;
  const step = 360 / Math.max(1, count);

  const [phase, setPhase] = useState("charging");
  const [scan, setScan] = useState(-1);
  const [target, setTarget] = useState(-1);

  const cardRefs = useRef([]);
  const angleRef = useRef(0);
  const speedRef = useRef(SPEED_START);
  const orbitingRef = useRef(true);
  const rafRef = useRef(null);

  const orbitPos = (i, angle) => {
    const a = ((i * step - 90 + angle) * Math.PI) / 180;
    return [CX + Math.cos(a) * RX, CY + Math.sin(a) * RY];
  };

  const place = (i, x, y, scale = 1, rotate = 0, opacity = 1) => {
    const el = cardRefs.current[i];
    if (!el) return;
    el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%) scale(${scale}) rotate(${rotate}deg)`;
    el.style.opacity = String(opacity);
  };

  const placeRing = () => {
    for (let i = 0; i < count; i++) {
      const [x, y] = orbitPos(i, angleRef.current);
      place(i, x, y);
    }
  };

  // On the ring before the first paint, not piled in a corner.
  useLayoutEffect(() => {
    placeRing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // The orbit, building speed for as long as the round is closing.
  useEffect(() => {
    let last = performance.now();
    const loop = (now) => {
      if (!orbitingRef.current) return;
      const dt = Math.min(64, now - last) / 1000;
      last = now;
      speedRef.current = Math.min(SPEED_MAX, speedRef.current + SPEED_RAMP * dt);
      angleRef.current = (angleRef.current + speedRef.current * dt) % 360;
      placeRing();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // A scan flickering across the board while it charges.
  useEffect(() => {
    if (phase !== "charging" || count < 2) return undefined;
    const t = setInterval(() => {
      setScan((prev) => {
        let next = Math.floor(Math.random() * count);
        if (next === prev) next = (next + 1) % count;
        return next;
      });
    }, SCAN_MS);
    return () => clearInterval(t);
  }, [phase, count]);

  useRevealSequence(
    async (ctx) => {
      const found = await ctx.waitForWinner();

      setScan(-1);
      setTarget(found);
      setPhase("locked");
      await ctx.sleep(LOCK_MS);

      setPhase("beam");
      await ctx.sleep(BEAM_MS);

      // Every card into the core, one after another.
      orbitingRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setPhase("absorb");
      const from = tiles.map((_, i) => orbitPos(i, angleRef.current));
      const total = ABSORB_MS + ABSORB_STAGGER * Math.max(0, count - 1);
      const started = performance.now();
      const absorbAt = (now) => {
        let done = true;
        for (let i = 0; i < count; i++) {
          const p = Math.min(
            1,
            Math.max(0, (now - started - i * ABSORB_STAGGER) / ABSORB_MS),
          );
          if (p < 1) done = false;
          const e = easeOut(p);
          const [fx, fy] = from[i];
          place(
            i,
            fx + (CX - fx) * e,
            fy + (CY - fy) * e,
            1 - 0.85 * e,
            360 * e,
            1 - e,
          );
        }
        return done;
      };
      // A browser stops calling requestAnimationFrame when the window is
      // not in front; the flight is raced against a plain timer so the
      // show still ends, and the cards are put where they finish either
      // way.
      const absorb = new Promise((resolve) => {
        const frame = (now) => {
          if (absorbAt(now)) resolve();
          else requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
      });
      await Promise.race([absorb, ctx.sleep(total + 120)]);
      absorbAt(started + total + 1);

      setPhase("portal");
      await ctx.sleep(PORTAL_MS);

      setPhase("won");
      await ctx.sleep(HOLD_MS);
      onFinished();
    },
    winnerIndex,
    [count],
  );

  const winner = target >= 0 ? tiles[target] : null;
  const lockedOn = phase === "locked" || phase === "beam";
  const opened = phase === "portal" || phase === "won";

  return (
    <div className="rv-np">
      {/* The sample's own room: deep blue, a dot grid, a cyan aura and
          ring with a light running round it - over the shared backdrop. */}
      <div className="rv-np-bg" aria-hidden="true" />
      <div className="rv-np-aura" aria-hidden="true" />
      <div className="rv-np-ring" aria-hidden="true" />
      <div
        className={`rv-np-spin ${phase !== "charging" ? "rv-np-spin--fast" : ""}`}
        aria-hidden="true"
      />

      <div className="rv-np-pill">
        {STATUS[phase]}
        {lockedOn && winner ? ` • ${winner.name}` : ""}
      </div>

      {tiles.map((tile, i) => (
        <div
          key={tile.id}
          ref={(el) => {
            cardRefs.current[i] = el;
          }}
          className={[
            "rv-np-card",
            i === scan ? "rv-np-card--scan" : "",
            i === target && lockedOn ? "rv-np-card--target" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="rv-np-icon">
            <img src={tile.img} alt={tile.name || ""} />
          </div>
          <div className="rv-np-name">{tile.name}</div>
        </div>
      ))}

      <div
        className={[
          "rv-np-core",
          phase === "absorb" ? "rv-np-core--overload" : "",
          opened ? "rv-np-core--gone" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="rv-np-core-icon">⚡</div>
        <div className="rv-np-core-text">Energy core</div>
      </div>

      {phase === "beam" || phase === "absorb" ? (
        <div className="rv-np-beam" aria-hidden="true" />
      ) : null}

      {opened ? (
        <>
          <div className="rv-np-lightning" aria-hidden="true">
            {Array.from({ length: 8 }, (_, i) => (
              <span key={i} style={{ transform: `rotate(${i * 45}deg)` }} />
            ))}
          </div>
          <div className="rv-np-flash" aria-hidden="true" />
          <Particles seed={target} />
        </>
      ) : null}

      {phase === "won" && winner ? (
        <div className="rv-np-winner">
          <div className="rv-np-crown">👑</div>
          <img src={winner.img} alt={winner.name || "winner"} />
          <div className="rv-np-winname">{winner.name}</div>
        </div>
      ) : null}
    </div>
  );
}
